// Single source of truth for error codes across the app.
//
// Each CODE is developer-facing (logged, returned in the API envelope, stored
// for debugging). Each entry maps it to a short human `name` (a label safe to
// show on the portal) and a longer user-facing `message`. `httpStatus` is the
// default HTTP status for API responses using that code.
//
// Backend sets the code (and optionally a more specific message); the frontend
// looks up the friendly name/message here. Provider failures (WhatsApp/Meta,
// Resend, Razorpay) are classified into the codes below so they group and read
// nicely instead of surfacing raw provider blobs.

export interface ErrorEntry {
  /** Short label safe to show as a status/title, e.g. "Number not allowed". */
  name: string
  /** User-facing sentence shown as failure feedback. */
  message: string
  /** Default HTTP status for API responses with this code. */
  httpStatus: number
}

export const ERROR_CATALOG = {
  // ---- request / auth / generic --------------------------------------------
  VALIDATION_ERROR: {
    name: 'Invalid input',
    message: 'Some details are invalid. Check the form and try again.',
    httpStatus: 400,
  },
  NOT_FOUND: {
    name: 'Not found',
    message: 'We couldn’t find what you were looking for.',
    httpStatus: 404,
  },
  UNAUTHORIZED: {
    name: 'Not signed in',
    message: 'Please sign in to continue.',
    httpStatus: 401,
  },
  FORBIDDEN: {
    name: 'Not allowed',
    message: 'You don’t have permission to do that.',
    httpStatus: 403,
  },
  CONFLICT: {
    name: 'Conflict',
    message: 'That action conflicts with the current state. Refresh and try again.',
    httpStatus: 409,
  },
  ROLE_CONFLICT: {
    name: 'Email already in use',
    message: 'This email belongs to an academy staff member and can’t be added as a parent.',
    httpStatus: 409,
  },
  RATE_LIMITED: {
    name: 'Too many requests',
    message: 'You’re going too fast. Wait a moment and try again.',
    httpStatus: 429,
  },
  FEATURE_NOT_AVAILABLE: {
    name: 'Not available on your plan',
    message: 'This feature isn’t included in your current plan.',
    httpStatus: 403,
  },
  ACADEMY_SUSPENDED: {
    name: 'Academy suspended',
    message: 'This academy is suspended. Contact support to restore access.',
    httpStatus: 403,
  },
  NOT_IMPLEMENTED: {
    name: 'Coming soon',
    message: 'This isn’t available yet.',
    httpStatus: 501,
  },
  INTERNAL_ERROR: {
    name: 'Something went wrong',
    message: 'Something went wrong on our end. Please try again.',
    httpStatus: 500,
  },

  // ---- payments ------------------------------------------------------------
  PAYMENT_FAILED: {
    name: 'Payment failed',
    message: 'The payment couldn’t be completed.',
    httpStatus: 402,
  },
  PAYMENT_PROVIDER_REJECTED: {
    name: 'Payment provider error',
    message: 'The payment provider rejected the request. Try again or pay in cash at the academy.',
    httpStatus: 502,
  },

  // ---- WhatsApp (Meta Cloud API) -------------------------------------------
  WHATSAPP_AUTH: {
    name: 'WhatsApp not configured',
    message: 'WhatsApp messaging isn’t set up correctly. Please contact support.',
    httpStatus: 502,
  },
  WHATSAPP_RECIPIENT_NOT_ALLOWED: {
    name: 'Number not allowed',
    message: 'This number can’t receive WhatsApp messages yet.',
    httpStatus: 422,
  },
  WHATSAPP_TEMPLATE_ERROR: {
    name: 'WhatsApp template problem',
    message: 'The WhatsApp message template was rejected.',
    httpStatus: 502,
  },
  WHATSAPP_INVALID_RECIPIENT: {
    name: 'Invalid phone number',
    message: 'The phone number looks invalid. Check the country code and number.',
    httpStatus: 422,
  },
  WHATSAPP_SEND_FAILED: {
    name: 'WhatsApp send failed',
    message: 'Couldn’t send the WhatsApp message. Please try again.',
    httpStatus: 502,
  },

  // ---- email (Resend) ------------------------------------------------------
  EMAIL_SEND_FAILED: {
    name: 'Email send failed',
    message: 'Couldn’t send the email. Please try again.',
    httpStatus: 502,
  },
} as const

export type ErrorCode = keyof typeof ERROR_CATALOG

const FALLBACK: ErrorEntry = {
  name: 'Error',
  message: 'Something went wrong. Please try again.',
  httpStatus: 500,
}

function entry(code: string): ErrorEntry {
  return (ERROR_CATALOG as Record<string, ErrorEntry>)[code] ?? FALLBACK
}

/** Short, portal-safe label for a code (e.g. "Number not allowed"). */
export function errorName(code: string): string {
  return entry(code).name
}

/** User-facing failure message for a code. */
export function errorMessage(code: string): string {
  return entry(code).message
}

/** Default HTTP status for a code. */
export function errorStatus(code: string): number {
  return entry(code).httpStatus
}

/** True if the code is a known catalog code (not a fallback). */
export function isKnownErrorCode(code: string): code is ErrorCode {
  return code in ERROR_CATALOG
}
